import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import ImageUploader from "@/components/ImageUploader";
import HelpTip from "@/components/HelpTip";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowUp, ArrowDown } from "lucide-react";

const empty = { title: "", subtitle: "", button_text: "Shop Now", button_link: "/shop", image_url: "", active: true, sort_order: 0 };
const inputCls = "w-full border-2 border-black p-2 font-mono text-sm";

export default function AdminBanners() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [open, setOpen] = useState(false);

    const load = async () => setItems((await api.get("/admin/banners")).data);
    useEffect(() => { load(); }, []);

    const save = async () => {
        if (!editing.image_url) return toast.error("Image is required");
        try {
            const payload = { ...editing, sort_order: Number(editing.sort_order) };
            if (editing.id) await api.put(`/admin/banners/${editing.id}`, payload);
            else await api.post("/admin/banners", payload);
            toast.success("Saved"); setOpen(false); load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };
    const del = async (id) => { if (!window.confirm("Delete this banner?")) return; await api.delete(`/admin/banners/${id}`); load(); };
    const toggle = async (b) => { await api.put(`/admin/banners/${b.id}`, { ...b, active: !b.active }); load(); };
    const move = async (b, dir) => {
        const sibling = items.find(x => dir === "up" ? x.sort_order < b.sort_order : x.sort_order > b.sort_order);
        if (!sibling) return;
        await api.put(`/admin/banners/${b.id}`, { ...b, sort_order: sibling.sort_order });
        await api.put(`/admin/banners/${sibling.id}`, { ...sibling, sort_order: b.sort_order });
        load();
    };

    return (
        <div data-testid="admin-banners-cms">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                    <h1 className="font-display text-4xl tracking-tighter">Banners</h1>
                    <HelpTip>Full-width banners for the homepage. Use for drops, collections, or seasonal campaigns. Only banners marked 'Live' appear.</HelpTip>
                </div>
                <button onClick={() => { setEditing({ ...empty, sort_order: items.length }); setOpen(true); }} className="bg-black text-white px-4 py-2 font-mono text-xs uppercase" data-testid="new-banner-cms-btn">+ New Banner</button>
            </div>
            <div className="space-y-3">
                {items.map(b => (
                    <div key={b.id} className="bg-white border-2 border-black p-4 grid grid-cols-[160px_1fr_auto] gap-4 items-center">
                        <div className="aspect-[16/9] bg-neutral-100 border border-black overflow-hidden">
                            {b.image_url ? <img src={b.image_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center font-mono text-[10px]">No image</div>}
                        </div>
                        <div>
                            <div className="font-display text-xl">{b.title || <span className="text-neutral-400">Untitled</span>}</div>
                            <div className="font-mono text-xs text-neutral-500">{b.subtitle}</div>
                            <div className="font-mono text-[10px] uppercase mt-1">→ {b.button_link}</div>
                        </div>
                        <div className="flex items-center gap-1">
                            <button onClick={() => move(b, "up")} title="Move up" className="p-2 border-2 border-black"><ArrowUp className="w-3 h-3" /></button>
                            <button onClick={() => move(b, "down")} title="Move down" className="p-2 border-2 border-black"><ArrowDown className="w-3 h-3" /></button>
                            <button onClick={() => toggle(b)} className={`px-3 py-2 font-mono text-xs uppercase border-2 border-black ${b.active ? "bg-black text-white" : "bg-white"}`}>{b.active ? "Live" : "Off"}</button>
                            <button onClick={() => { setEditing({ ...b }); setOpen(true); }} className="px-3 py-2 font-mono text-xs uppercase border-2 border-black">Edit</button>
                            <button onClick={() => del(b.id)} className="px-3 py-2 font-mono text-xs uppercase border-2 border-black">Del</button>
                        </div>
                    </div>
                ))}
                {items.length === 0 && <div className="border-2 border-black p-10 text-center font-mono text-xs">No banners yet. Click "+ New Banner" to create one.</div>}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="border-2 border-black max-w-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-display text-2xl">{editing?.id ? "Edit" : "New"} Banner</DialogTitle></DialogHeader>
                    {editing && (
                        <div className="space-y-3">
                            <ImageUploader value={editing.image_url} onChange={v => setEditing({ ...editing, image_url: v })} recommended="Recommended: 1920 × 800 (hero widescreen)" aspect="16/9" />
                            <input placeholder="Title (optional)" value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} className={inputCls} />
                            <input placeholder="Subtitle (optional)" value={editing.subtitle} onChange={e => setEditing({ ...editing, subtitle: e.target.value })} className={inputCls} />
                            <div className="grid grid-cols-2 gap-2">
                                <input placeholder="Button text" value={editing.button_text} onChange={e => setEditing({ ...editing, button_text: e.target.value })} className={inputCls} />
                                <input placeholder="Button link (/shop or https://...)" value={editing.button_link} onChange={e => setEditing({ ...editing, button_link: e.target.value })} className={inputCls} />
                            </div>
                            <label className="flex items-center gap-2"><input type="checkbox" checked={editing.active} onChange={e => setEditing({ ...editing, active: e.target.checked })} /> <span className="font-mono text-xs uppercase">Live</span></label>
                        </div>
                    )}
                    <DialogFooter><button onClick={save} className="bg-black text-white px-4 py-2 font-mono text-xs uppercase">Save</button></DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
